// Copyright (C) 2025, Meritic Inc.

import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Button,
  Box,
  Progress,
  Alert,
  AlertIcon,
  Spinner,
  Code,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  TreeSelectionStep,
  ReviewStep
} from "./PromptGenerationSteps";
import showToast from "hooks/useCustomToast";
import { generatePrompt, newtonApi } from "services/EvaServices";
import { GenerateSystemPromptRequest } from "build-ts/agent_config";
import { Header } from "build-ts/metadata";
import { AnalysisTreeProto } from "build-ts/analysis";

interface PromptGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentType: string;
  basePrompt: string;
  onPromptGenerated: (prompt: string) => void;
  analysis_flag?: string;
}

// Removed StepData interface - using direct state management

const PromptGenerationModal: React.FC<PromptGenerationModalProps> = ({
  isOpen,
  onClose,
  agentType,
  basePrompt,
  onPromptGenerated,
  analysis_flag = "",
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedPrompt, setGeneratedPrompt] = useState<string | null>(null);
  const [showGeneratedPromptModal, setShowGeneratedPromptModal] = useState(false);

  // Data state
  const [trees, setTrees] = useState<Header[]>([]);
  const [selectedTree, setSelectedTree] = useState<Header | null>(null);
  const [selectedTreeData, setSelectedTreeData] = useState<AnalysisTreeProto | null>(null);

  const getTotalSteps = (): number => {
    // Only TIGA agent type supported
    return 2; // Tree selection + Review
  };

  const totalSteps = getTotalSteps();

  const loadInitialData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Only TIGA agent type is supported
      if (agentType === "TIGA") {
        const data = await newtonApi.getTrees();
        setTrees(data.trees);
      } else {
        throw new Error("Only TIGA agent type is supported for prompt generation");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load data";
      setError(errorMessage);
      showToast("Error", errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  }, [agentType]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setError(null);
      setGeneratedPrompt(null);
      setSelectedTree(null);
      setSelectedTreeData(null);
    }
  }, [isOpen]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen, loadInitialData]);

  const getStepTitle = (step: number): string => {
    if (step === 1) return "Select Analysis Tree";
    return "Review & Generate";
  };

  const canProceed = (): boolean => {
    // Check if we're on the last step (review)
    if (currentStep === totalSteps) {
      return true; // Always allow generation on review step
    }

    // For tree selection step, need exactly one tree selected
    if (currentStep === 1) {
      return selectedTree !== null;
    }

    return true;
  };

  const handleNext = async () => {
    if (currentStep < totalSteps) {
      // If moving to review step, fetch the selected tree data
      if (currentStep === 1 && selectedTree?.id) {
        setIsLoading(true);
        try {
          const treeData = await newtonApi.getTreeById(selectedTree.id);
          setSelectedTreeData(treeData);
          setCurrentStep(currentStep + 1);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Failed to load tree data";
          setError(errorMessage);
          showToast("Error", errorMessage, "error");
        } finally {
          setIsLoading(false);
        }
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      handleGenerate();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTreeData) {
      setError("No tree data available for generation");
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const request: GenerateSystemPromptRequest = {
        agent_type: "TIGA",
        prompt: basePrompt,
        analysis_flag: analysis_flag,
        agent_params: JSON.stringify({ tree: AnalysisTreeProto.toJSON(selectedTreeData) }),
      } as any;

      const response = await generatePrompt(request);

      if (response) {
        setGeneratedPrompt(response);
        setShowGeneratedPromptModal(true);
        // Close the main modal when showing the generated prompt modal
        onClose();
        showToast("Success", "System prompt generated successfully!", "success");
      } else {
        throw new Error("Failed to generate prompt - no content received");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to generate prompt";
      setError(errorMessage);
      showToast("Error", errorMessage, "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleCloseGeneratedPromptModal = () => {
    setShowGeneratedPromptModal(false);
    setGeneratedPrompt(null);
  };

  const renderCurrentStep = () => {
    if (currentStep === 1) {
      return (
        <TreeSelectionStep
          trees={trees}
          selectedTree={selectedTree}
          onSelectionChange={setSelectedTree}
        />
      );
    }

    // Review step (always last)
    return (
      <ReviewStep
        agentType={agentType}
        basePrompt={basePrompt}
        selectedTree={selectedTree}
        selectedTreeData={selectedTreeData}
        generatedPrompt={generatedPrompt}
        isGenerating={isGenerating}
      />
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="xl" closeOnOverlayClick={false} isCentered>
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            <VStack spacing={2} align="stretch">
              <Text fontSize="lg" fontWeight="bold">
                Generate System Prompt - TIGA Agent
              </Text>
              <Text fontSize="sm" color="gray.600">
                Generate system prompt for TIGA agent using analysis tree
              </Text>
              <HStack spacing={4}>
                <Text fontSize="sm" color="gray.500">
                  Step {currentStep} of {totalSteps}
                </Text>
                <Progress
                  value={(currentStep / totalSteps) * 100}
                  size="sm"
                  colorScheme="blue"
                  flex="1"
                />
              </HStack>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6} maxH="70vh" overflowY="auto">
            {isLoading ? (
              <Box textAlign="center" py={8}>
                <Spinner size="lg" color="blue.500" />
                <Text mt={4} color="gray.600">Loading data...</Text>
              </Box>
            ) : error ? (
              <Alert status="error">
                <AlertIcon />
                <Text>{error}</Text>
              </Alert>
            ) : (
              <VStack spacing={6} align="stretch">
                <Text fontSize="md" fontWeight="semibold" color="gray.700">
                  {getStepTitle(currentStep)}
                </Text>

                {renderCurrentStep()}

                <HStack justify="space-between" pt={4}>
                  <Button
                    leftIcon={<ChevronLeftIcon />}
                    onClick={handlePrevious}
                    isDisabled={currentStep === 1}
                    variant="outline"
                  >
                    Previous
                  </Button>

                  {!generatedPrompt && (
                    <Button
                      rightIcon={currentStep === totalSteps ? undefined : <ChevronRightIcon />}
                      onClick={handleNext}
                      isDisabled={!canProceed() || isLoading}
                      colorScheme="blue"
                      isLoading={isGenerating || isLoading}
                      loadingText={isGenerating ? "Generating..." : "Loading..."}
                    >
                      {currentStep === totalSteps ? "Generate Prompt" : "Next"}
                    </Button>
                  )}
                </HStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Generated Prompt Modal */}
      <Modal isOpen={showGeneratedPromptModal} onClose={handleCloseGeneratedPromptModal} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            <Text fontSize="lg" fontWeight="bold">
              Generated Prompt for {agentType}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              {generatedPrompt && (
                <Box
                  maxH="500px"
                  overflowY="auto"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                  bg="white"
                  position="relative"
                >
                  <Code
                    fontSize="sm"
                    whiteSpace="pre-wrap"
                    p={4}
                    display="block"
                    w="100%"
                    fontFamily="mono"
                    lineHeight="1.5"
                  >
                    {generatedPrompt}
                  </Code>
                </Box>
              )}

              <HStack justify="flex-end" pt={4}>
                <Button
                  variant="outline"
                  onClick={handleCloseGeneratedPromptModal}
                >
                  Close
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={() => {
                    if (generatedPrompt) {
                      navigator.clipboard.writeText(generatedPrompt);
                      showToast("Success", "Prompt copied to clipboard!", "success");
                    }
                  }}
                  isDisabled={!generatedPrompt}
                >
                  Copy Prompt
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PromptGenerationModal;
